import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import { Capacitor } from "@capacitor/core";
import { createPortal } from "react-dom";
import CoverScreen from "../pages/CoverScreen"; // Keep CoverScreen separate
import { ConfirmDialog } from "../../app/components/SharedComponents";
import {
  persistAuthSession,
  removeAuthSession,
  restoreAuthSession,
} from "../../utils/persistentStorage";
import {
  buildSessionSnapshot,
  hasCompleteSession,
} from "../../utils/authSessionPersistence";
import { classifyAuthFailureReason } from "../../utils/authSessionFailure";
import API_BASE_URL, { buildAssetUrl } from "../config/config.server";
import { initializePushNotifications, unregisterPushNotifications } from "../../services/pushNotificationService";
import "../styles/animations.css"; // Import the CSS file

const AuthContext = createContext();
const HAS_SEEN_LANDING_KEY = "hasSeenLandingPage";
const POST_LOGIN_TRANSITION_KEY = "showPostLoginTransition";
const MAX_TIMEOUT_MS = 2147483647;
const SESSION_VALIDATION_INTERVAL_MS = 20000;

export const AuthProvider = ({ children }) => {
  const [operatorID, setOperatorID] = useState(null);
  const [operatorData, setOperatorData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showCoverScreen, setShowCoverScreen] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [showSessionExpiredPopup, setShowSessionExpiredPopup] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(
    "Your session has expired. Please log in again to continue."
  );
  const [showDeletedAccount, setShowDeletedAccount] = useState(false);
  const [authNotice, setAuthNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionRestored, setSessionRestored] = useState(false);

  // User profile state for real-time updates
  const [userProfile, setUserProfile] = useState(null);
  const [profileVersion, setProfileVersion] = useState(0); // Force re-render when profile updates

  const sessionExpiryTimer = useRef(null);
  const sessionValidationTimer = useRef(null);

  const clearTimers = () => {
    if (sessionExpiryTimer.current) {
      clearTimeout(sessionExpiryTimer.current);
      sessionExpiryTimer.current = null;
    }

    if (sessionValidationTimer.current) {
      clearInterval(sessionValidationTimer.current);
      sessionValidationTimer.current = null;
    }
  };

  const normalizeToken = (rawToken) => {
    if (!rawToken || typeof rawToken !== "string") return "";
    return rawToken.startsWith("Bearer ") ? rawToken.slice(7) : rawToken;
  };

  const isNativePlatform = () => Capacitor.isNativePlatform();

  const getStorageSnapshot = () => ({
    operatorID:
      sessionStorage.getItem("operatorID") ||
      localStorage.getItem("operatorID") ||
      "",
    sessionToken:
      sessionStorage.getItem("sessionToken") ||
      localStorage.getItem("sessionToken") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      "",
    expiresAt:
      sessionStorage.getItem("expiresAt") ||
      localStorage.getItem("expiresAt") ||
      "",
  });

  const getActiveToken = async () => {
    const sessionToken = normalizeToken(sessionStorage.getItem("sessionToken"));
    if (sessionToken) return sessionToken;

    const localToken = normalizeToken(
      localStorage.getItem("sessionToken") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("token")
    );
    if (localToken) return localToken;

    if (isNativePlatform()) {
      const storedSession = await restoreAuthSession();
      return normalizeToken(storedSession.sessionToken);
    }

    return "";
  };

  const getOperatorIdFromToken = (rawToken) => {
    const token = normalizeToken(rawToken);
    if (!token || !token.includes(".")) return "";

    try {
      const tokenPart = token.split(".")[1];
      const normalized = tokenPart.replace(/-/g, "+").replace(/_/g, "/");
      const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
      const decoded = JSON.parse(atob(padded));
      const opId = decoded?.opID ?? decoded?.OpID ?? decoded?.user_id ?? decoded?.id;
      return opId === undefined || opId === null ? "" : String(opId).trim();
    } catch (_error) {
      return "";
    }
  };

  const isAdminImpersonationToken = (rawToken) => {
    const token = normalizeToken(rawToken);
    if (!token || !token.includes(".")) return false;

    try {
      const tokenPart = token.split(".")[1];
      const normalized = tokenPart.replace(/-/g, "+").replace(/_/g, "/");
      const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
      const decoded = JSON.parse(atob(padded));
      return Boolean(decoded?.adminImpersonation || decoded?.admin_impersonation);
    } catch (_error) {
      return false;
    }
  };

  const scheduleSessionExpiryPopup = (expiresAt) => {
    if (sessionExpiryTimer.current) {
      clearTimeout(sessionExpiryTimer.current);
      sessionExpiryTimer.current = null;
    }

    if (!expiresAt) return;

    const expiryTime = new Date(expiresAt).getTime();
    if (Number.isNaN(expiryTime)) return;

    const delay = expiryTime - Date.now();
    if (delay <= 0) {
      triggerSessionExpiredPopup();
      return;
    }

    if (delay > MAX_TIMEOUT_MS) {
      sessionExpiryTimer.current = window.setTimeout(() => {
        scheduleSessionExpiryPopup(expiresAt);
      }, MAX_TIMEOUT_MS);
      return;
    }

    sessionExpiryTimer.current = window.setTimeout(() => {
      triggerSessionExpiredPopup();
    }, delay);
  };

  // API request helper function
  const apiRequest = async (method, url, data = null, options = {}) => {
    const token = await getActiveToken();

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const isForm =
      typeof options === "boolean"
        ? options
        : Boolean(options?.isForm);

    const headers = {
      Authorization: `Bearer ${token}`,
      ...(isForm ? {} : { "Content-Type": "application/json" }),
      ...((options && typeof options === "object" && options.headers) || {}),
    };

    const body = data !== null && data !== undefined
      ? isForm || data instanceof FormData
        ? data
        : JSON.stringify(data)
      : undefined;

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method,
        headers,
        body,
      });
      return await response.json();
    } catch (error) {
      console.error(`❌ ${method} ${url} failed:`, error);
      throw error;
    }
  };

  // Fetch user profile from API
  const fetchUserProfile = async () => {
    try {
      if (!operatorID) return null;

      // Check if we have cached profile data first
      const cachedProfile = localStorage.getItem(`userProfile_${operatorID}`);
      if (cachedProfile) {
        try {
          const parsed = JSON.parse(cachedProfile);
          // Check if cache is less than 5 minutes old
          if (parsed.timestamp && Date.now() - parsed.timestamp < 300000) {
            console.log(
              "✅ [AuthContext] Using cached profile:",
              parsed.profile
            );
            setUserProfile(parsed.profile);
            return parsed.profile;
          }
        } catch (e) {
          // Invalid cached data, continue with fetch
        }
      }

      console.log(
        "🔄 [AuthContext] Fetching user profile for operatorID:",
        operatorID
      );

      const activeToken = await getActiveToken();
      const response = await fetch(
        `${API_BASE_URL}/api/profile/${operatorID}`,
        {
          headers: {
            Authorization: `Bearer ${activeToken}`,
          },
        }
      );

      let result = {};
      try {
        result = await response.json();
      } catch (error) {
        console.warn("⚠️ Profile response was not valid JSON:", error);
      }

      // Normalize auth failures so stale/invalid sessions do not look like deleted accounts.
      if (!response.ok) {
        const reason = classifyAuthFailureReason(response.status, {
          message: result?.message || response.statusText,
        });

        if (reason === "deleted-account") {
          console.error("❌ Account appears deleted while fetching profile for operatorID:", operatorID);
          sessionStorage.removeItem("operatorID");
          sessionStorage.removeItem("sessionToken");
          sessionStorage.removeItem("expiresAt");
          setOperatorID(null);
          setIsAuthenticated(false);
          setUserProfile(null);
          setShowDeletedAccount(true);
        } else {
          console.warn("⚠️ Profile fetch reported an auth failure; clearing stale session without showing deleted-account landing.", response.status, response.statusText);
          clearTimers();
          sessionStorage.removeItem("operatorID");
          sessionStorage.removeItem("sessionToken");
          sessionStorage.removeItem("expiresAt");
          localStorage.removeItem("operatorID");
          localStorage.removeItem("sessionToken");
          localStorage.removeItem("authToken");
          localStorage.removeItem("token");
          localStorage.removeItem("deletedAccount");
          setOperatorID(null);
          setIsAuthenticated(false);
          setUserProfile(null);
          setShowDeletedAccount(false);
          setShowSessionExpiredPopup(true);
          setSessionExpiredMessage("Your session is no longer active. Please log in again.");
        }
        throw new Error(`Profile fetch failed: ${response.statusText}`);
      }
      const userData =
        result?.data?.profile ||
        result?.data?.data ||
        result?.data ||
        result?.profile ||
        result || {};

      console.log("✅ [AuthContext] Fetched user profile:", userData);

      const normalizedKycStatus = String(
        userData.kycStatus || userData.status || userData.verificationStatus || ''
      ).toLowerCase();
      const profile = {
          name:
            userData.fullName ||
            userData.firstName ||
            userData.businessName ||
            `User ${operatorID}`,
          avatar:
            buildAssetUrl(
              userData.avatar || userData.avatar_url || userData.AvatarUrl
            ) || "null",
          fullName:
            userData.fullName || userData.firstName || `User ${operatorID}`,
          username: userData.username || userData.userName || `user${operatorID}`,
          bio: userData.bio || userData.Bio || '',
          email: userData.email || userData.emailAddress || '',
          phone: userData.phone || userData.phoneNumber || '',
          gst:
            userData.gst ||
            userData.gstNumber ||
            userData.GSTNumber ||
            userData?.businessGst ||
            '',
          gender: userData.gender || userData.Gender || '',
          address:
            userData.address ||
            userData.businessAddress ||
            userData.officeAddress ||
            userData.location ||
            '',
          verified:
            Boolean(userData.verified) || normalizedKycStatus === 'verified',
          kycStatus:
            userData.kycStatus || userData.status || userData.verificationStatus ||
            (Boolean(userData.verified) ? 'verified' : ''),
      };

      // Cache the profile for 5 minutes
      localStorage.setItem(
        `userProfile_${operatorID}`,
        JSON.stringify({
          profile,
          timestamp: Date.now(),
        })
      );

      setUserProfile(profile);
      // Clear deleted-account flag if we successfully fetched a profile
      setShowDeletedAccount(false);
      return profile;
    } catch (error) {
      console.error("❌ Error fetching user profile:", error);
      return null;
    }
  };

  // Refresh user profile and notify all components
  const refreshUserProfile = async () => {
    console.log("🔄 [AuthContext] Refreshing user profile...");

    // Clear cached profile to force fresh fetch
    if (operatorID) {
      localStorage.removeItem(`userProfile_${operatorID}`);
    }

    const profile = await fetchUserProfile();
    if (profile) {
      setProfileVersion((prev) => prev + 1); // Force re-render in components
      console.log(
        "✅ [AuthContext] Profile refreshed, version:",
        profileVersion + 1
      );
    }
    return profile;
  };

  // Validate if stored session is still valid in the database
  const validateStoredSession = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/test`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        console.error("❌ Stored session validation failed (401/403). Session is stale.");
        sessionStorage.removeItem("operatorID");
        sessionStorage.removeItem("sessionToken");
        sessionStorage.removeItem("expiresAt");
        return false;
      }

      if (!response.ok) {
        console.warn("⚠️ Session validation returned an unexpected status, keeping the session for recovery:", response.status, response.statusText);
        return true;
      }

      console.log("✅ Stored session validated successfully");
      return true;
    } catch (error) {
      console.warn("⚠️ Session validation could not complete right now; keeping the session for recovery:", error);
      return true;
    }
  };

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const sessionStorageSnapshot = getStorageSnapshot();
        const persistedSession = isNativePlatform()
          ? await restoreAuthSession()
          : {};

        const finalSession = buildSessionSnapshot(
          sessionStorageSnapshot,
          persistedSession
        );

        if (hasCompleteSession(finalSession)) {
          sessionStorage.setItem("operatorID", finalSession.operatorID);
          sessionStorage.setItem("sessionToken", finalSession.sessionToken);
          sessionStorage.setItem("expiresAt", finalSession.expiresAt);
          localStorage.setItem("operatorID", finalSession.operatorID);
          localStorage.setItem("sessionToken", finalSession.sessionToken);
          localStorage.setItem("expiresAt", finalSession.expiresAt);
          localStorage.setItem("authToken", finalSession.sessionToken);
          localStorage.setItem("token", finalSession.sessionToken);
        }

        if (!hasCompleteSession(finalSession)) {
          // If no session is available but a deleted-account marker exists, show the deleted-account landing
          try {
            const deletedFlag = localStorage.getItem("deletedAccount");
            if (deletedFlag === "1") {
              setShowDeletedAccount(true);
            }
          } catch (e) {
            // ignore
          }

          setSessionRestored(true);
          setLoading(false);
          return;
        }

        const expiryTime = new Date(finalSession.expiresAt).getTime();
        if (!Number.isNaN(expiryTime) && expiryTime <= Date.now()) {
          console.warn("⚠️ Stored session has expired, clearing it.");
          sessionStorage.removeItem("operatorID");
          sessionStorage.removeItem("sessionToken");
          sessionStorage.removeItem("expiresAt");
          setOperatorID(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        console.log("✅ [AuthContext] Restoring persisted session for OpID:", finalSession.operatorID);
        setOperatorID(finalSession.operatorID);
        setIsAuthenticated(true);
        scheduleSessionExpiryPopup(finalSession.expiresAt);
        setSessionRestored(true);

        // Do not force a startup logout here. The active-session validator below will handle
        // real auth failures once the app is running, while the persisted 30-day session
        // remains usable across iOS app relaunches.
      } catch (error) {
        console.error("[AuthContext] restoreSession failed:", error);
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, []);

  const showNotice = (message, type = "info", duration = 2200) => {
    setAuthNotice({ message, type });
    window.setTimeout(() => {
      setAuthNotice(null);
    }, duration);
  };

  const triggerSessionExpiredPopup = (message) => {
    setSessionExpiredMessage(
      message || "Your session has expired. Please log in again to continue."
    );
    setShowSessionExpiredPopup(true);
    setShowLogoutPopup(false);
  };

  const validateSession = async (messageOnInvalid) => {
    if (!isAuthenticated) return;

    const token = await getActiveToken();
    if (!token) {
      triggerSessionExpiredPopup(
        messageOnInvalid || "Your session is no longer active. Please login again."
      );
      return;
    }
  };

  // Mark current device/session as deleted locally without performing a full logout redirect.
  const markDeletedAccount = () => {
    try {
      // Clear persisted tokens but do not force a full page reload
      sessionStorage.removeItem("operatorID");
      sessionStorage.removeItem("sessionToken");
      sessionStorage.removeItem("expiresAt");
      localStorage.removeItem("operatorID");
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("authToken");
      localStorage.removeItem("token");
      localStorage.setItem("deletedAccount", "1");
    } catch (e) {
      // ignore
    }

    // Clear in-memory auth state and timers
    clearTimers();
    setOperatorID(null);
    setUserProfile(null);
    setIsAuthenticated(false);
    setSessionRestored(false);

    // Hide any session/logout popups since this is a deliberate deleted-account flow
    setShowSessionExpiredPopup(false);
    setShowLogoutPopup(false);

    // Show the deleted-account landing
    setShowDeletedAccount(true);
  };

  useEffect(() => {
    const handleSessionExpired = (event) => {
      triggerSessionExpiredPopup(event?.detail?.message);
    };

    window.addEventListener("app-session-expired", handleSessionExpired);
    return () => {
      window.removeEventListener("app-session-expired", handleSessionExpired);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !sessionRestored) {
      return;
        console.log('[AuthContext] markDeletedAccount: clearing local session state and showing deleted landing');
    }

    const startupDelay = window.setTimeout(() => {
      validateSession();

      sessionValidationTimer.current = window.setInterval(() => {
        validateSession();
      }, SESSION_VALIDATION_INTERVAL_MS);
    }, 5000);

    return () => {
      window.clearTimeout(startupDelay);

      if (sessionValidationTimer.current) {
        clearInterval(sessionValidationTimer.current);
        sessionValidationTimer.current = null;
      }
    };
  }, [isAuthenticated, sessionRestored]);

  useEffect(() => {
    if (!operatorID || !isAuthenticated) {
      return;
    }

    fetchUserProfile();
  }, [operatorID, isAuthenticated]);

  useEffect(() => {
    const initializePushOnRestore = async () => {
      if (!operatorID || !isAuthenticated || !sessionRestored) return;
      if (!isNativePlatform()) return;

      const token = await getActiveToken();
      if (!token) return;

      try {
        await initializePushNotifications(token);
        console.log('[AuthContext] Push notifications initialized on restored session');
      } catch (error) {
        console.warn('[AuthContext] Failed to initialize push on restore:', error);
      }
    };

    initializePushOnRestore();
  }, [operatorID, isAuthenticated, sessionRestored]);

  useEffect(() => {
    const onStorage = (event) => {
      if (!isAuthenticated) return;
      if (event.storageArea !== localStorage) return;
      if (event.key !== "sessionToken" && event.key !== "authToken") return;
      if (!event.newValue) return;

      const incomingToken = normalizeToken(event.newValue);
      const currentToken = normalizeToken(sessionStorage.getItem("sessionToken"));
      if (!incomingToken || !currentToken) return;
      if (incomingToken === currentToken) return;

      const incomingOperatorId = getOperatorIdFromToken(incomingToken);
      const currentOperatorId = String(
        sessionStorage.getItem("operatorID") || operatorID || ""
      ).trim();

      // Ignore token changes for different users in the same browser storage.
      if (!incomingOperatorId || !currentOperatorId) return;
      if (incomingOperatorId !== currentOperatorId) return;

      // If either the current or incoming token is an admin impersonation session,
      // keep the admin session active. Admin impersonation should not force logout
      // when the real user signs in separately on the same OpID.
      const incomingIsImpersonation = isAdminImpersonationToken(incomingToken);
      const currentIsImpersonation = isAdminImpersonationToken(currentToken);
      if (incomingIsImpersonation || currentIsImpersonation) return;

      triggerSessionExpiredPopup(
        "You were logged out because your account was used in another tab or device."
      );
    };

    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, [isAuthenticated, operatorID]);

  const login = (id, token, expiresAt) => {
    console.log(`[AuthContext] User logging in with ID: ${id}`);
    const parsedExpiry = Date.parse(String(expiresAt || ""));
    const normalizedExpiresAt = !Number.isNaN(parsedExpiry)
      ? new Date(parsedExpiry).toISOString()
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

    setOperatorID(id);
    setIsAuthenticated(true);
    setShowDeletedAccount(false);
    setSessionRestored(true);
    try { localStorage.removeItem("deletedAccount"); } catch (e) { /* ignore */ }
    setShowSessionExpiredPopup(false);
    sessionStorage.setItem("operatorID", id);
    sessionStorage.setItem("sessionToken", token);
    sessionStorage.setItem("expiresAt", normalizedExpiresAt);
    localStorage.setItem("operatorID", id);
    localStorage.setItem("sessionToken", token);
    localStorage.setItem("authToken", token);
    localStorage.setItem("token", token);
    localStorage.setItem("expiresAt", normalizedExpiresAt);
    scheduleSessionExpiryPopup(normalizedExpiresAt);
    sessionStorage.setItem(POST_LOGIN_TRANSITION_KEY, "1");
    showNotice("Signed in successfully", "success");

    if (isNativePlatform()) {
      persistAuthSession({
        operatorID: id,
        sessionToken: token,
        expiresAt: normalizedExpiresAt,
      }).catch((err) => {
        console.error("[AuthContext] persistAuthSession failed:", err);
      });
    }

    // Fetch user profile after login
    setTimeout(() => {
      fetchUserProfile();
    }, 100);

    console.log(`[AuthContext] Login completed for user: ${id}`);
  };

  const handleLogout = async () => {
    console.log(`[AuthContext] User logging out: ${operatorID}`);
    setShowLogoutPopup(false);
    setShowSessionExpiredPopup(false);
    setShowDeletedAccount(false);

    try {
      const currentToken = await getActiveToken();
      if (currentToken) {
        await fetch(`${API_BASE_URL}/api/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentToken}`,
          },
          body: JSON.stringify({
            opID: operatorID,
            sessionToken: currentToken,
          }),
        });
      } else {
        console.warn("[AuthContext] No active session token available during logout; skipping backend session deletion.");
      }
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      // Clear session storage (per-tab)
      sessionStorage.clear();
      
      if (isNativePlatform()) {
        removeAuthSession().catch((err) => {
          console.error("[AuthContext] removeAuthSession failed:", err);
        });
      }
      
      // Also clear localStorage to remove any stale data
      localStorage.removeItem("operatorID");
      localStorage.removeItem("sessionToken");
      localStorage.removeItem("authToken");
      localStorage.removeItem("token");
      localStorage.removeItem("expiresAt");
      localStorage.removeItem("deletedAccount");
      localStorage.setItem(HAS_SEEN_LANDING_KEY, "true");

      if (typeof window !== "undefined") {
        window.setTimeout(() => {
          try {
            window.location.assign("/");
          } catch (error) {
            window.location.reload();
          }
        }, 0);
      }
      
      // Unregister push notifications
      try {
        await unregisterPushNotifications();
      } catch (error) {
        console.warn("Error unregistering push notifications:", error);
      }
      
      setOperatorID(null);
      setOperatorData(null);
      setIsAuthenticated(false);
      setLoading(false);
      setSessionRestored(true);
      setShowCoverScreen(false);
      clearTimers();
      showNotice("Signed out", "info");
      console.log(`[AuthContext] Logout completed, all state cleared`);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        operatorID,
        operatorData,
        isAuthenticated,
        sessionRestored,
        login,
        handleLogout,
        triggerSessionExpiredPopup,
        setShowSessionExpiredPopup,
        showSessionExpiredPopup,
        setShowLogoutPopup,
        loading,
        userProfile,
        profileVersion,
        refreshUserProfile,
        apiRequest,
        showDeletedAccount,
        setShowDeletedAccount,
        markDeletedAccount,
      }}
    >
      {children}

      {/* Logout Confirmation Popup */}
      {showLogoutPopup &&
        typeof document !== "undefined" &&
        createPortal(
          <>
            <div className="auth-logout-overlay"></div>
            <div className="auth-logout-popup" role="dialog" aria-modal="true" aria-labelledby="auth-logout-title">
              <h3 id="auth-logout-title" className="popup-title">Confirm Logout</h3>
              <p className="popup-text">
                You are about to end your current session. Continue?
              </p>
              <div className="popup-actions">
                <button
                  className="cancel-button"
                  onClick={() => setShowLogoutPopup(false)}
                >
                  Stay Logged In
                </button>
                <button className="yes-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          </>,
          document.body
        )}

      {showSessionExpiredPopup && (
        <ConfirmDialog
          open={showSessionExpiredPopup}
          title="Session Expired"
          description={sessionExpiredMessage}
          confirmLabel="Login Again"
          hideCancel
          variant="warning"
          onConfirm={handleLogout}
          onCancel={() => {}}
        />
      )}

      {authNotice && (
        <div
          className={`auth-status-toast ${authNotice.type === "success" ? "auth-status-toast-success" : "auth-status-toast-info"}`}
          role="status"
          aria-live="polite"
        >
          {authNotice.message}
        </div>
      )}

      {showCoverScreen && (
        <CoverScreen key="cover-screen" onContinue={handleLogout} />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
